import { type ReactNode } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useConfirmDialog } from "@/providers/confirmDialogContext";
import {
  footerButtonMetrics,
  footerCancelButtonSx,
  footerPrimaryButtonSx,
} from "@/shared/ui/dialogFooterButtons";
import { darkDialogContentSx, darkDialogHeaderSx } from "@/shared/ui/dialogSurfaceStyles";

// ─── Size presets ────────────────────────────────────────────────────────────

export type AppModalSize =
  | "compact"   // xs + min(400px, 80vh) — confirms, simple name forms
  | "standard"  // xs + min(560px, 80vh) — default for most dialogs
  | "wide"      // sm + min(640px, 80vh) — paste areas, medium content
  | "full"      // md + min(800px, 90vh) — advanced search (accordion layout)
  | "xl"        // lg + min(85vh, 920px) — advanced search (left-nav layout)
  // escape hatch — omit maxHeight to let the dialog size to its content;
  // maxWidth: false removes MUI's breakpoint cap (pair with paperSx for a custom width).
  | { maxWidth?: "xs" | "sm" | "md" | "lg" | false; maxHeight?: string | number };

type SizeValues = { muiMaxWidth: "xs" | "sm" | "md" | "lg" | false; maxHeight?: string };

const MODAL_HEADER_HEIGHT_PX = 60;
const MODAL_HEADER_ICON_SIZE_PX = 20;
const MODAL_HEADER_TITLE_FONT_SIZE_PX = 13;
const MODAL_HEADER_TITLE_GAP_PX = 8;
const MODAL_CLOSE_BUTTON_SIZE_PX = 28;
const MODAL_CLOSE_ICON_SIZE_PX = 20;
const MODAL_CLOSE_BUTTON_RADIUS_PX = 6;
const MODAL_FOOTER_HEIGHT_PX = 59;

export function resolveSizeValues(size: AppModalSize): SizeValues {
  if (size === "compact")  return { muiMaxWidth: "xs", maxHeight: "min(400px, 80vh)" };
  if (size === "standard") return { muiMaxWidth: "xs", maxHeight: "min(560px, 80vh)" };
  if (size === "wide")     return { muiMaxWidth: "sm", maxHeight: "min(640px, 80vh)" };
  if (size === "full")     return { muiMaxWidth: "md", maxHeight: "min(800px, 90vh)" };
  if (size === "xl")       return { muiMaxWidth: "lg", maxHeight: "min(85vh, 920px)" };
  const maxHeight = typeof size.maxHeight === "number" ? `${size.maxHeight}px` : size.maxHeight;
  return {
    muiMaxWidth: size.maxWidth ?? "sm",
    ...(maxHeight ? { maxHeight } : {}),
  };
}

/** A single entry of MUI's `sx` array form: never an array itself. */
type SxEntry = Exclude<SxProps<Theme>, readonly unknown[]> | boolean;

/** Normalizes MUI's `sx` prop (object, array, or absent) into an array for spreading. */
function toSxArray(sx: SxProps<Theme> | undefined): SxEntry[] {
  if (Array.isArray(sx)) {
    return [...sx] as SxEntry[];
  }
  return sx ? [sx as SxEntry] : [];
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ModalHeader({
  title,
  subtitle,
  icon,
  headerContent,
  onClose,
  sx,
}: Readonly<{
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  /** Replaces the icon + title cluster. The close button stays. */
  headerContent?: ReactNode;
  onClose: () => void;
  sx?: SxProps<Theme>;
}>) {
  return (
    <Box
      sx={[
        (theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxSizing: "border-box",
          width: "100%",
          height: MODAL_HEADER_HEIGHT_PX,
          minHeight: MODAL_HEADER_HEIGHT_PX,
          flexShrink: 0,
          pl: "22px",
          pr: 2,
          backgroundColor: theme.app.dialog.headerBackground,
          ...darkDialogHeaderSx(theme),
          color: theme.app.dialog.headerTextColor,
          borderBottom: `1px solid ${theme.app.dialog.borderColor}`,
          // Match Dialog paper `borderRadius: 2` so the header follows the
          // rounded corners when paper overflow is not clipped.
          borderTopLeftRadius: 2,
          borderTopRightRadius: 2,
        }),
        ...toSxArray(sx),
      ]}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          gap: `${MODAL_HEADER_TITLE_GAP_PX}px`,
          flex: 1,
          minWidth: 0,
          "& .MuiSvgIcon-root": {
            fontSize: MODAL_HEADER_ICON_SIZE_PX,
            width: MODAL_HEADER_ICON_SIZE_PX,
            height: MODAL_HEADER_ICON_SIZE_PX,
            color: theme.app.dialog.headerIconColor,
            flexShrink: 0,
          },
        })}
      >
        {headerContent ?? (
          <>
            {icon}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: "var(--font-sans)",
                  fontWeight: 600,
                  fontSize: MODAL_HEADER_TITLE_FONT_SIZE_PX,
                  lineHeight: 1.25,
                  color: "inherit",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  variant="caption"
                  sx={(theme) => ({ color: theme.app.dialog.headerSubtitleColor, display: "block" })}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
      <IconButton
        size="small"
        disableRipple
        onClick={onClose}
        aria-label="Close"
        sx={(theme) => ({
          width: MODAL_CLOSE_BUTTON_SIZE_PX,
          height: MODAL_CLOSE_BUTTON_SIZE_PX,
          minWidth: MODAL_CLOSE_BUTTON_SIZE_PX,
          p: 0,
          borderRadius: `${MODAL_CLOSE_BUTTON_RADIUS_PX}px`,
          color: theme.app.dialog.headerCloseIconColor,
          flexShrink: 0,
          "& .MuiSvgIcon-root": {
            fontSize: MODAL_CLOSE_ICON_SIZE_PX,
            width: MODAL_CLOSE_ICON_SIZE_PX,
            height: MODAL_CLOSE_ICON_SIZE_PX,
          },
          "&:hover": {
            borderRadius: `${MODAL_CLOSE_BUTTON_RADIUS_PX}px`,
            backgroundColor: theme.app.dialog.headerCloseHoverBackground,
          },
        })}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );
}

/** Styled footer bar shared by the standard buttons and custom `footer` content. */
function ModalFooterBar({
  children,
  fullScreen = false,
  sx,
}: Readonly<{ children: ReactNode; fullScreen?: boolean; sx?: SxProps<Theme> }>) {
  return (
    <Box
      sx={[
        (theme) => ({
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
          height: MODAL_FOOTER_HEIGHT_PX,
          minHeight: MODAL_FOOTER_HEIGHT_PX,
          maxHeight: MODAL_FOOTER_HEIGHT_PX,
          px: 2,
          py: 0,
          borderTop: 1,
          borderColor: theme.app.dialog.footerBorderColor,
          flexShrink: 0,
          backgroundColor: theme.app.dialog.footerBackground,
          borderBottomLeftRadius: fullScreen ? 0 : 2,
          borderBottomRightRadius: fullScreen ? 0 : 2,
        }),
        ...toSxArray(sx),
      ]}
    >
      {children}
    </Box>
  );
}

function StandardFooterButtons({
  onCancel,
  cancelLabel,
  onConfirm,
  confirmLabel,
  confirmDisabled,
  confirmDestructive,
  onDelete,
  deleteDisabled,
  deleteLabel,
}: Readonly<{
  onCancel: () => void;
  cancelLabel: string;
  onConfirm?: () => void;
  confirmLabel?: string;
  confirmDisabled?: boolean;
  confirmDestructive?: boolean;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  deleteLabel: string;
}>) {
  return (
    <>
      {onDelete && (
        <Button
          variant="outlined"
          size="small"
          color="error"
          onClick={onDelete}
          disabled={deleteDisabled}
          startIcon={<DeleteOutlinedIcon />}
          sx={footerButtonMetrics()}
        >
          {deleteLabel}
        </Button>
      )}
      <Box sx={{ flex: 1 }} />
      <Button variant="outlined" size="small" onClick={onCancel} sx={footerCancelButtonSx}>
        {cancelLabel}
      </Button>
      <Button
        variant="contained"
        size="small"
        disableElevation
        onClick={onConfirm}
        disabled={confirmDisabled}
        sx={(theme) => footerPrimaryButtonSx(theme, confirmDestructive)}
      >
        {confirmLabel}
      </Button>
    </>
  );
}

// ─── AppModal ─────────────────────────────────────────────────────────────────

export interface AppModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** Rendered before the title inside the dark header bar. */
  headerIcon?: ReactNode;
  /** Replaces the icon and title inside the dark header bar. The close button stays. */
  headerContent?: ReactNode;
  size?: AppModalSize;
  /**
   * Custom footer content rendered inside the styled footer bar, replacing the
   * standard Cancel/Confirm/Delete buttons. When omitted, `confirmLabel` and
   * `onConfirm` are required to render the standard footer.
   */
  footer?: ReactNode;
  confirmLabel?: string;
  onConfirm?: () => void;
  confirmDisabled?: boolean;
  /** Styles the confirm button as destructive. For dialogs whose confirm action is the delete. */
  confirmDestructive?: boolean;
  cancelLabel?: string;
  /** When provided, a Delete button is rendered. Clicking it triggers a confirmation before calling this. */
  onDelete?: () => Promise<void> | void;
  deleteDisabled?: boolean;
  deleteLabel?: string;
  deleteConfirmTitle?: string;
  deleteConfirmMessage?: string;
  /**
   * When true, the footer bar is not rendered at all.
   * Use when the content area owns its own footer (e.g. the Advanced Search left-nav layout).
   */
  hideFooter?: boolean;
  /** Overrides merged on top of the default DialogContent styles. */
  contentSx?: SxProps<Theme>;
  /** Overrides merged on top of the default Dialog paper styles (e.g. custom width). */
  paperSx?: SxProps<Theme>;
  /** Overrides merged on top of the default header bar. */
  headerSx?: SxProps<Theme>;
  /** Overrides merged on top of the footer bar. Desktop callers can leave this unset. */
  footerSx?: SxProps<Theme>;
  /** Phone layout: the dialog fills the screen. Leave false on large screens. */
  fullScreen?: boolean;
  children: ReactNode;
}

export function getDeleteConfirmOptions(
  deleteLabel: string,
  deleteConfirmTitle: string,
  deleteConfirmMessage: string,
) {
  return {
    title: deleteConfirmTitle,
    message: deleteConfirmMessage,
    confirmLabel: deleteLabel,
    cancelLabel: "Cancel" as const,
    destructive: true,
  };
}

export default function AppModal({
  open,
  onClose,
  title,
  subtitle,
  headerIcon,
  headerContent,
  size = "standard",
  footer,
  confirmLabel,
  onConfirm,
  confirmDisabled,
  confirmDestructive,
  cancelLabel = "Cancel",
  onDelete,
  deleteDisabled,
  deleteLabel = "Delete",
  deleteConfirmTitle = "Are you sure?",
  deleteConfirmMessage = "This action cannot be undone.",
  hideFooter = false,
  contentSx,
  paperSx,
  headerSx,
  footerSx,
  fullScreen = false,
  children,
}: Readonly<AppModalProps>) {
  const confirm = useConfirmDialog();
  const { muiMaxWidth, maxHeight } = resolveSizeValues(size);

  const handleDelete = async () => {
    const confirmed = await confirm(
      getDeleteConfirmOptions(deleteLabel, deleteConfirmTitle, deleteConfirmMessage),
    );
    if (confirmed) {
      await onDelete?.();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={muiMaxWidth}
      fullWidth
      fullScreen={fullScreen}
      slotProps={{
        paper: {
          sx: [
            (theme) => ({
              // Ring via box-shadow so it isn't clipped by overflow and does
              // not shrink the header below the paper width.
              border: "none",
              boxShadow: `0 0 0 1px ${theme.app.dialog.borderColor}, var(--Paper-shadow)`,
              borderRadius: 2,
              ...(maxHeight ? { maxHeight } : {}),
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }),
            ...toSxArray(paperSx),
          ],
        },
      }}
    >
      <ModalHeader
        title={title}
        subtitle={subtitle}
        icon={headerIcon}
        headerContent={headerContent}
        onClose={onClose}
        sx={headerSx}
      />

      <DialogContent
        sx={[
          {
            px: 2,
            py: 2,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          },
          darkDialogContentSx,
          ...toSxArray(contentSx),
        ]}
      >
        {children}
      </DialogContent>

      {!hideFooter && (
        <ModalFooterBar fullScreen={fullScreen} sx={footerSx}>
          {footer ?? (
            <StandardFooterButtons
              onCancel={onClose}
              cancelLabel={cancelLabel}
              onConfirm={onConfirm}
              confirmLabel={confirmLabel}
              confirmDisabled={confirmDisabled}
              confirmDestructive={confirmDestructive}
              onDelete={onDelete ? () => void handleDelete() : undefined}
              deleteDisabled={deleteDisabled}
              deleteLabel={deleteLabel}
            />
          )}
        </ModalFooterBar>
      )}
    </Dialog>
  );
}
